import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { CampaignApi } from '../services/CampaignService';

export default function InstagramCampaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      const rows = await CampaignApi.list();
      setCampaigns(rows || []);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to load campaigns');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    if (!name.trim()) return;
    setBusy(true);
    setError('');
    try {
      await CampaignApi.create(name.trim());
      setName('');
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Create failed');
    } finally {
      setBusy(false);
    }
  };

  const runLifecycle = async (id, action) => {
    setBusy(true);
    setError('');
    try {
      if (action === 'ready') await CampaignApi.markReady(id);
      if (action === 'clear') await CampaignApi.clearReady(id);
      if (action === 'deactivate') await CampaignApi.deactivate(id);
      await load();
    } catch (e) {
      setError(
        e?.response?.data?.message || e.message || `Action ${action} failed`,
      );
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this campaign?')) return;
    setBusy(true);
    setError('');
    try {
      await CampaignApi.remove(id);
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1100 }}>
      <Typography variant="h5" gutterBottom>
        Instagram Campaigns
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Create campaigns, mark READY (one per teacher), then open a campaign to
        add Use Cases (keyword → send_dm) and PDF files. Media attaches on first
        matching comment while READY.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {Array.isArray(error) ? error.join(', ') : String(error)}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <TextField
            label="Campaign name"
            size="small"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') create();
            }}
          />
          <Button
            variant="contained"
            onClick={create}
            disabled={busy || !name.trim()}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Add Campaign
          </Button>
        </Stack>
      </Paper>

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {campaigns.length === 0 && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography color="text.secondary">No campaigns yet.</Typography>
                </TableCell>
              </TableRow>
            )}
            {campaigns.map((c) => (
              <TableRow key={c.id} hover>
                <TableCell>{c.id}</TableCell>
                <TableCell>
                  <Button
                    component={RouterLink}
                    to={`/teacher/bot/instagram/campaigns/${c.id}`}
                    size="small"
                  >
                    {c.name}
                  </Button>
                </TableCell>
                <TableCell>
                  <Chip size="small" label={c.status} />
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                    {(c.status === 'new' || c.status === 'inactive') && (
                      <Button
                        size="small"
                        disabled={busy}
                        onClick={() => runLifecycle(c.id, 'ready')}
                      >
                        Mark READY
                      </Button>
                    )}
                    {c.status === 'ready' && (
                      <Button
                        size="small"
                        disabled={busy}
                        onClick={() => runLifecycle(c.id, 'clear')}
                      >
                        Clear READY
                      </Button>
                    )}
                    {c.status === 'active' && (
                      <Button
                        size="small"
                        color="warning"
                        disabled={busy}
                        onClick={() => runLifecycle(c.id, 'deactivate')}
                      >
                        Deactivate
                      </Button>
                    )}
                    <Button
                      size="small"
                      color="error"
                      disabled={busy || c.status === 'active' || c.status === 'ready'}
                      onClick={() => remove(c.id)}
                    >
                      Delete
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
